import { createThunkSlice } from 'src/redux/ReduxUtils';
import { PayloadAction } from '@reduxjs/toolkit';
import { SurveyFile } from 'src/types/Questionnaire/QuestionnaireTypes';
import DeletedDocumentsService from 'src/services/DeletedDocumentsService';

// Highlight interface
export interface FileHighlight {
  fileId: number;
  timestamp: number;
  type: 'success' | 'info' | 'error';
}

// State interface
interface DeletedFilesState {
  files: SurveyFile[];
  loading: boolean;
  error: string | null;
  restoreLoading: { [fileId: number]: boolean };
  restoreError: { [fileId: number]: string | null };
  lastFetchedCertificationId: number | null;
  fileHighlights: FileHighlight[];
}

// Initial state
const initialState: DeletedFilesState = {
  files: [],
  loading: false,
  error: null,
  restoreLoading: {},
  restoreError: {},
  lastFetchedCertificationId: null,
  fileHighlights: [],
};

// Slice using modern createThunkSlice pattern
const deletedFilesSlice = createThunkSlice({
  name: 'deletedDocuments',
  initialState,
  reducers: (create) => {
    return {
      // Fetch deleted files async thunk
      fetchDeletedFiles: create.asyncThunk(
        async (certificationId: number) => {
          const response = await DeletedDocumentsService.fetchDeletedFiles(
            certificationId
          );
          return response.data || [];
        },
        {
          pending: (state) => {
            state.loading = true;
            state.error = null;
          },
          fulfilled: (state, action) => {
            state.loading = false;
            state.files = action.payload;
            state.error = null;
            state.lastFetchedCertificationId = action.meta.arg;
          },
          rejected: (state, action) => {
            state.loading = false;
            state.error = action.error.message || 'Failed to fetch trash files';
            state.files = [];
          },
        }
      ),

      // Restore file async thunk
      restoreFile: create.asyncThunk(
        async (fileId: number) => {
          const response = await DeletedDocumentsService.restoreFile(fileId);
          return {
            fileId,
            message: response.data?.message || 'File restored successfully',
            restoration_type: response.data?.restoration_type || 'automatic',
          };
        },
        {
          pending: (state, action) => {
            const fileId = action.meta.arg;
            state.restoreLoading[fileId] = true;
            state.restoreError[fileId] = null;
          },
          fulfilled: (state, action) => {
            const fileId = action.payload.fileId;
            state.restoreLoading[fileId] = false;
            // Remove the restored file from the trash list
            state.files = state.files.filter((file) => file.id !== fileId);
          },
          rejected: (state, action) => {
            const fileId = action.meta.arg;
            state.restoreLoading[fileId] = false;
            state.restoreError[fileId] =
              action.error.message || 'Failed to restore file';
          },
        }
      ),

      // Regular reducers
      clearDeletedFiles: create.reducer((state) => {
        state.files = [];
        state.error = null;
        state.restoreError = {};
        state.lastFetchedCertificationId = null;
      }),

      clearRestoreError: create.reducer(
        (state, action: PayloadAction<number>) => {
          delete state.restoreError[action.payload];
        }
      ),

      clearAllErrors: create.reducer((state) => {
        state.error = null;
        state.restoreError = {};
      }),

      // Add deleted file from WebSocket event
      addDeletedFile: create.reducer(
        (state, action: PayloadAction<SurveyFile>) => {
          const file = action.payload;
          // Check if file already exists in the list
          const existingIndex = state.files.findIndex((f) => f.id === file.id);
          if (existingIndex === -1) {
            state.files = [...state.files, file];
          } else {
            // Update existing file
            state.files[existingIndex] = file;
          }
        }
      ),

      // Remove deleted file (when restored via WebSocket)
      removeDeletedFile: create.reducer(
        (state, action: PayloadAction<number>) => {
          const fileId = action.payload;
          state.files = state.files.filter((file) => file.id !== fileId);
        }
      ),

      // Add file highlight
      addFileHighlight: create.reducer(
        (
          state,
          action: PayloadAction<{
            fileId: number;
            type: 'success' | 'info' | 'error';
          }>
        ) => {
          const newHighlight: FileHighlight = {
            fileId: action.payload.fileId,
            timestamp: Date.now(),
            type: action.payload.type,
          };
          state.fileHighlights = [...state.fileHighlights, newHighlight];
        }
      ),

      // Clear expired highlights
      clearExpiredHighlights: create.reducer(
        (state, action: PayloadAction<FileHighlight>) => {
          state.fileHighlights = state.fileHighlights.filter(
            (highlight) => highlight.fileId !== action.payload.fileId
          );
        }
      ),
    };
  },
});

export const {
  fetchDeletedFiles,
  restoreFile,
  clearDeletedFiles,
  clearRestoreError,
  clearAllErrors,
  addDeletedFile,
  removeDeletedFile,
  addFileHighlight,
  clearExpiredHighlights,
} = deletedFilesSlice.actions;
export default deletedFilesSlice.reducer;
